/*
 * App shell.
 *   Phone     bottom tab bar (Home · Campaigns · Results · Assistant)
 *   Tablet+   icon rail (search, sections, account menu at the bottom) + content
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { endpoints } from "../api/endpoints";
import type { Overview } from "../api/types";
import { Avatar, BackChevron, BeaconMark, Chevron, Counter, FieldRow, I, Row, Section, Segmented } from "../components/tk";
import { FallbackMainButton, Sheet } from "../components/tk/overlays";
import { chatTime, plural } from "../lib/format";
import { useLayout } from "../lib/hooks";
import { haptic, isTelegram, loadThemePref, setThemePref, type ThemePref } from "../lib/tg";
import { DemoBar } from "../demo/DemoBar";
import { CommandPalette } from "./CommandPalette";
import { MobileNavigation, type MobileBack } from "./MobileNavigation";
import { activeKey, navGroup, NAV } from "./nav";
import { useSession, wk } from "./session";

export function useOverview(range: Overview["range"] = "7d") {
  const { ws } = useSession();
  return useQuery({ queryKey: wk(ws, "overview", range), queryFn: () => endpoints.overview(ws, range), refetchInterval: 60_000 });
}
function useNavItems() {
  const { membership } = useSession();
  return NAV.filter((n) => !(n.staff && membership.role === "client_viewer"));
}

export function Shell({ children }: { children: ReactNode }) {
  const layout = useLayout();
  const navigate = useNavigate();
  const [mobileBack, setMobileBack] = useState<MobileBack | null>(null);
  const [palette, setPalette] = useState(false);
  const [workspace, setWorkspace] = useState(false);
  const { membership } = useSession();
  const loc = useLocation();
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
  useEffect(() => {
    if (layout === "phone") window.scrollTo(0, 0);
  }, [loc.pathname, layout]);

  return (
    <MobileNavigation.Provider value={setMobileBack}>
    <div className="app">
      {layout !== "phone" && (
        <a href="#main" className="skip-link">
          Skip to content
        </a>
      )}
      <DemoBar />
      {layout !== "phone" && <Rail onSearch={() => setPalette(true)} />}
      <main className={clsx("app-main", layout === "phone" && "app-scroll")} id="main" tabIndex={-1}>
        {layout === "phone" && <div className="mobile-tools">
          {loc.pathname === "/" ? <button type="button" className="workspace-button" onClick={() => setWorkspace(true)} aria-label="Workspace and account">
            <Avatar name={membership.name} size={26} /><span>{membership.name}</span><Chevron />
          </button> : <button type="button" className="navbar-back" onClick={() => {
            if (mobileBack?.pathname === loc.pathname) mobileBack.action();
            else navigate(loc.pathname.slice(0, loc.pathname.lastIndexOf("/")) || "/");
          }}><BackChevron />Back</button>}
          <button type="button" className="navbar-icon" aria-label="Search Beacon" onClick={() => setPalette(true)}><I.search /></button>
        </div>}
        {children}
      </main>
      {layout === "phone" && <TabBar />}
      <FallbackMainButton />
      <WorkspaceSheet open={workspace} onClose={() => setWorkspace(false)} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </div>
    </MobileNavigation.Provider>
  );
}

/* ---------------- phone tab bar ---------------- */

function TabBar() {
  const { data } = useOverview();
  const loc = useLocation();
  const key = navGroup(loc.pathname);
  const { membership } = useSession();
  const viewer = membership.role === "client_viewer";
  const tabs = [NAV.find((n) => n.key === "home")!, NAV.find((n) => n.key === "campaigns")!, NAV.find((n) => n.key === "results")!,
    viewer ? { key: "workspace", to: "/settings", label: "Workspace", icon: "gear" as const } : NAV.find((n) => n.key === "copilot")!];
  return (
    <nav className="tk-tabs" aria-label="Main">
      {tabs.map((t) => {
        const Icon = I[t.icon];
        const on = t.key === key;
        return (
          <Link key={t.key} to={t.to} className={clsx(on && "tk-on")} aria-current={on ? "page" : undefined} onClick={() => haptic.select()}>
            <Icon strokeWidth={on ? 2.2 : 1.8} />
            <span className="tk-cap">{t.label}</span>
            {t.key === "copilot" && !!data?.pending_count && <span className="tk-pip" aria-label={`${data.pending_count} pending approvals`}>{data.pending_count}</span>}
          </Link>
        );
      })}
    </nav>
  );
}

/* ---------------- rail (tablet and desktop) ---------------- */

const RAIL_ORDER = ["home", "approvals", "copilot", "ads", "campaigns", "rules", "agents", "results", "attribution", "channels"];
const RAIL_LABELS: Record<string, string> = { copilot: "Copilot", attribution: "Attribution" };

function Rail({ onSearch }: { onSearch: () => void }) {
  const { data } = useOverview();
  const { membership } = useSession();
  const loc = useLocation();
  const key = activeKey(loc.pathname);
  const [ws, setWs] = useState(false);
  const sync = data?.account?.sync.status;
  return (
    <aside className="rail" aria-label="Main">
      <span className="rail-mark" aria-hidden="true">
        <BeaconMark size={34} />
      </span>
      <button type="button" onClick={onSearch} aria-label="Search (⌘K)" title="Search (⌘K)">
        <I.search />
        <span>Search</span>
      </button>
      {useNavItems().filter((n) => RAIL_ORDER.includes(n.key)).sort((a, b) => RAIL_ORDER.indexOf(a.key) - RAIL_ORDER.indexOf(b.key)).map((n) => {
        const Icon = I[n.icon];
        const on = n.key === key;
        return (
          <Link key={n.key} to={n.to} className={clsx(on && "on")} aria-current={on ? "page" : undefined}>
            <Icon strokeWidth={on ? 2.1 : 1.8} />
            <span>{RAIL_LABELS[n.key] ?? n.label}</span>
            {n.key === "approvals" && !!data?.pending_count && <span className="tk-pip" aria-label={`${data.pending_count} pending`}>{data.pending_count}</span>}
          </Link>
        );
      })}
      <button type="button" className="rail-account" onClick={() => setWs(true)} aria-label={`${membership.name}: workspace and account`} title={membership.name}>
        <span className="relative">
          <Avatar name={membership.name} size={32} />
          {sync && <span className={clsx("rail-sync", sync === "ok" ? "bg-green" : sync === "stale" ? "bg-orange" : "bg-red")} aria-hidden="true" />}
        </span>
      </button>
      <WorkspaceSheet open={ws} onClose={() => setWs(false)} />
    </aside>
  );
}

/* ---------------- workspace & account sheet ---------------- */

export function WorkspaceSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, ws, setWs, membership } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [theme, setTheme] = useState<ThemePref>(() => loadThemePref());
  const account = useOverview().data?.account;
  const create = useMutation({
    mutationFn: () => endpoints.createWorkspace({ name, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    onSuccess: async (w) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      setWs(w.id);
      setCreating(false);
      setName("");
      onClose();
      navigate("/onboarding");
    },
  });
  const logout = useMutation({
    mutationFn: endpoints.logout,
    onSuccess: () => {
      qc.clear();
      location.href = import.meta.env.BASE_URL;
    },
  });
  return (
    <Sheet open={open} onClose={onClose} title="Workspaces">
      <Section>
        {me.workspaces.map((w) => (
          <Row
            key={w.id}
            glyph={<Avatar name={w.name} size={36} />}
            inset="lg"
            label={w.name}
            sub={`${w.role.replace("_", " ")} · ${w.plan_sku}${w.pending_approvals ? ` · ${plural(w.pending_approvals, "approval")}` : ""}`}
            after={w.id === ws ? <I.check width={20} height={20} className="text-accent flex-none" /> : <Counter n={w.pending_approvals} muted />}
            chevron={false}
            onClick={() => {
              setWs(w.id);
              onClose();
              navigate("/");
            }}
          />
        ))}
        {creating ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <FieldRow label="Name" value={name} onChange={setName} placeholder="Acme Growth" autoFocus />
            <div className="flex gap-2 px-4 pb-3">
              <button type="button" className="tk-chip" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="submit" className="tk-chip tk-primary" disabled={!name.trim() || create.isPending}>
                Create
              </button>
            </div>
          </form>
        ) : (
          <Row glyph={<span className="tk-glyph" style={{ background: "var(--tk-fill)", color: "var(--tk-link)", width: 36, height: 36, borderRadius: "50%" }}><I.plus width={20} height={20} /></span>} inset="lg" label="New workspace" accent chevron={false} onClick={() => setCreating(true)} />
        )}
      </Section>
      {account && (
        <Section header="Ad account">
          <Row
            glyph={<span className={clsx("mx-1.5 size-2.5 rounded-full", account.sync.status === "ok" ? "bg-green" : account.sync.status === "stale" ? "bg-orange" : "bg-red")} aria-hidden="true" />}
            label={account.name}
            sub={`${account.sync.status === "ok" ? "Synced" : account.sync.status === "stale" ? "Sync delayed · last" : "Sync failed · last"} ${chatTime(account.sync.last_synced_at)}`}
            chevron={false}
          />
        </Section>
      )}
      <Section header="Workspace tools">
        {membership.role !== "client_viewer" && <Row label="Billing & balance" chevron onClick={() => { onClose(); navigate("/billing"); }} />}
        {membership.role !== "client_viewer" && <Row label="Clients & reports" chevron onClick={() => { onClose(); navigate("/agency"); }} />}
        <Row label="Settings" chevron onClick={() => { onClose(); navigate("/settings"); }} />
        <Row label="Notifications" chevron onClick={() => { onClose(); navigate("/notifications"); }} />
        {membership.role !== "client_viewer" && <Row label="Emergency stop" destructive chevron onClick={() => { onClose(); navigate("/settings/controls"); }} />}
      </Section>
      {!isTelegram && (
        <Section header="Appearance">
          <div className="px-3 py-2.5">
            <Segmented<ThemePref>
              label="Theme"
              className="m-0"
              items={[
                { value: "system", label: "Auto" },
                { value: "light", label: "Light" },
                { value: "dark", label: "Dark" },
              ]}
              value={theme}
              onChange={(t) => {
                setTheme(t);
                setThemePref(t);
              }}
            />
          </div>
        </Section>
      )}
      <Section header="Account" footer={me.session.surface === "miniapp" ? "Signed in from the Telegram Mini App." : undefined}>
        <Row glyph={<Avatar name={me.user.name} src={me.user.photo_url} size={36} />} inset="lg" label={me.user.name} sub={me.user.username ? `@${me.user.username}` : me.user.email ?? ""} />
        {!isTelegram && <Row glyph={<span className="tk-glyph" style={{ background: "var(--tk-fill)", color: "var(--tk-destructive)", width: 36, height: 36, borderRadius: "50%" }}><I.logout width={19} height={19} /></span>} inset="lg" label="Log out" destructive chevron={false} onClick={() => logout.mutate()} />}
      </Section>
    </Sheet>
  );
}
